/**
 * Document list row (v2.5 UX overhaul, v2.6 hover toolbar).
 *
 * Entire row is the click target — opens the document in the editor.
 *
 * Desktop (≥769px): action buttons (download, regenerate, delete) appear
 * on hover, replacing the date column. The overflow menu is hidden.
 *
 * Mobile (<768px): action buttons are hidden. The "..." overflow menu
 * provides access to the same actions.
 */

import OverflowMenu from "./OverflowMenu.jsx";

const dateFormat = new Intl.DateTimeFormat(undefined, {
  month: "short",
  day: "numeric",
  year: "numeric",
});

function formatSize(bytes) {
  if (!bytes || bytes < 1024) return `${bytes || 0} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

const BADGE_CLASS = {
  research: "badge--research",
  upload: "badge--upload",
  agent: "badge--agent",
};

export default function DocumentRow({
  doc,
  type,
  displayTitle,
  regenerating = false,
  onOpen,
  onDownload,
  onRegenerate,
  onDelete,
}) {
  const handleRowClick = () => onOpen?.(doc.filename);
  const handleKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onOpen?.(doc.filename);
    }
  };

  const modified = doc.lastModified
    ? dateFormat.format(new Date(doc.lastModified))
    : null;

  /* Shared action handlers — used by both desktop toolbar and mobile menu */
  const handleDownload = (e) => {
    e?.stopPropagation?.();
    onDownload(doc.filename);
  };
  const handleRegenerate = (e) => {
    e?.stopPropagation?.();
    onRegenerate(doc);
  };
  const handleDelete = (e) => {
    e?.stopPropagation?.();
    if (confirm(`Delete "${doc.filename}"?`)) onDelete(doc.filename);
  };

  const canRegenerate = onRegenerate && !regenerating;

  /* Overflow menu items — used for mobile "..." menu */
  const items = [];
  if (onDownload) items.push({ label: "Download", onClick: handleDownload });
  if (canRegenerate) items.push({ label: "Regenerate", onClick: handleRegenerate });
  if (onDelete) items.push({ label: "Delete", danger: true, onClick: handleDelete });

  const hasActions = items.length > 0;

  return (
    <li
      className="document-row"
      role={onOpen ? "button" : undefined}
      tabIndex={onOpen ? 0 : undefined}
      onClick={onOpen ? handleRowClick : undefined}
      onKeyDown={onOpen ? handleKeyDown : undefined}
    >
      <span className="document-row__name">{displayTitle || doc.filename}</span>
      {type && (
        <span className={`document-row__badge ${BADGE_CLASS[type] || ""}`}>
          {type}
        </span>
      )}
      <span className="document-row__size">{formatSize(doc.size)}</span>

      {/* Date — visible by default, hidden on hover (desktop) */}
      {modified && <span className="document-row__date">{modified}</span>}

      {regenerating && (
        <span
          className="document-row-spinner"
          role="status"
          aria-label="Regenerating"
        />
      )}

      {/* Desktop hover toolbar — hidden by default, shown on row hover */}
      {hasActions && (
        <div className="document-row__actions">
          {onDownload && (
            <button
              className="document-row__action-btn"
              title="Download"
              aria-label="Download"
              onClick={handleDownload}
            >
              ↓
            </button>
          )}
          {canRegenerate && (
            <button
              className="document-row__action-btn"
              title="Regenerate"
              aria-label="Regenerate"
              onClick={handleRegenerate}
            >
              ↻
            </button>
          )}
          {onDelete && (
            <button
              className="document-row__action-btn document-row__action-btn--danger"
              title="Delete"
              aria-label="Delete"
              onClick={handleDelete}
            >
              ×
            </button>
          )}
        </div>
      )}

      {/* Mobile overflow menu — hidden on desktop */}
      {hasActions && <OverflowMenu items={items} />}
    </li>
  );
}
